import React, { useEffect, useRef, useState } from 'react';
import Helmet from 'react-helmet';
import {
    Portlet, PortletBody,
    PortletHeader, PortletHeaderToolbar
} from "../../partials/content/Portlet";

import { Box, Grid, Stack } from "@mui/material";
import { routes } from '../../router/Routes';

import makeStyles from '@mui/styles/makeStyles';
import { BreadCrumb } from '../../common/Breadcrumb';

import { TableLevelAction } from '../../common';
import { AcknowledgeDialog } from '../ClaimAnalysisPage/AcknowledgeDialog';
import { FlagDialog } from '../ClaimAnalysisPage/FlagDialog';

import { useData, useStatePtr } from '@pure-ptr/react';
import { useFitHeight } from '../../../_metronic/layout/Layout';
import { pageStore, SearchTitle, useGlobalStore } from '../../common';
import { hideOrUnHideClaimFromDashboard } from '../../store/api/claimAnalysis';
import { SEARCH_PERIOD_ID } from '../../store/constants/settings';
import { ClaimStatistics } from '../ClaimAnalysisPage/ClaimStatistics';
import { ProcessClaimDialog } from '../ClaimAnalysisPage/ProcessDialog';
import { IgnoreFiltersDialog } from './IgnoreFiltersDialog';
import UpdateStatusDialog from '../ClaimAnalysisPage/UpdateStatusDialog';
import { TriageDataGrid } from './TriageGrid';
import { TriageTopFilters } from './TriageTopFilters';

const useStyles = makeStyles(theme => ({
    button: {
        marginTop: theme.spacing(3),
        marginBottom: theme.spacing(1),
        marginRight: theme.spacing(1),
        zIndex: 100,
    },
    formControl: {
        margin: theme.spacing(1),
        width: 200,
    },
    formControlClaim: {
        margin: theme.spacing(1),
        width: 71
    },
    formControlStatus: {
        marginTop: theme.spacing(1),
        marginBottom: theme.spacing(1),
        marginLeft: 4,
        marginRight: 4,
        width: 140,
        '@media (max-width: 1440px)': {
            width: 110,
        }
    },
    popover: {
        pointerEvents: 'none',
    },
    paper: {
        pointerEvents: 'auto',
        width: 300,
    },
    switch: {
        marginRight: 0,
        marginTop: 20,
        marginLeft: 0
    },
    gridMDMargin: {
        '@media (max-width: 1023px)': {
            paddingLeft: 10,
        }
    },
    IconButton: {
        padding: 6
    },
    statisticsContainer:{
        padding:'20px',
        paddingBottom:0
    }
}));

const { usePageStore, useGridFilters } = pageStore('triage-landing', {
    sort: [{ id: 'totalSummaryVariance', direction: 'desc' }]
});

export default function ConfidenceScoreTriagePage({
    intl,
    claimStatusList,
    hasClaimProcessorAccess, 
    defaultCustomFilters, 
    selectedClientName,
    pending,
    showHidden,
    actions: {
        claimAnalysisLanding,
        claimAnalysisSetDrilldown,
        downloadAnalysisReport,
        searchGetByName,
        searchReset,
        flagClaims,
        getTinyContracts,
        getPayersTiny,
        getClaimStatuses,
        getNpiRoles,
        resetClaimProcessError,
        resetPagedTable,
        setFlagToggle,
        setBulkFlagged,
        setOpenPlayDialog,
        setBulkAcknowledge,
        setMulti,
        resetCriteriaWaiting,
        getFacilities,
        getConfidenceMeasures
    }
} ) {
    useFitHeight();
    const isSummary= window.location.href.includes("fromSummary")

    const getByNamePending = useGlobalStore( x => x.search.getByName.pending );

    const claimAnalysisDownloadReportPending = useGlobalStore( x => x.claimAnalysis.download.pending );

    const claimsSearchTerm = usePageStore( x => x.searchTerm );


    const claimsData = usePageStore((x) => x.list);
    const significance = usePageStore( x => x.significance );

    const relativeFilter = useGlobalStore( x => 
        x.settings.data.find( setting => 
            setting.id === SEARCH_PERIOD_ID
        )
        ?.value ?? '' 
    );

    const classes = useStyles();
    const firstUpdate = useRef(true);
    const [refresh, setRefresh] = useState(Promise.resolve());
    const includeHiddenPtr = useStatePtr(showHidden || false);
    const hideClaimPtr = useStatePtr(null);


    useEffect(() => {
        getTinyContracts();
    }, [getTinyContracts]);

    useEffect(() => {
        getClaimStatuses();
    }, [getClaimStatuses]);

    useEffect(() => {
        getPayersTiny();
    }, [getPayersTiny]);
    useEffect(() => {
        getNpiRoles()
    }, [getNpiRoles]);

    useEffect(() => {
        getFacilities();
    }, [getFacilities]);

    useEffect(() => {
        getConfidenceMeasures();
    }, [getConfidenceMeasures]);

    useEffect(() => {
        if(isSummary) {
            resetCriteriaWaiting()
            return;
        } //We do not need any criteria filters if we are fetching based on summary filters//         
         const pageName = {
            name: 'triage-landing'
        }
        searchGetByName(pageName);

        return function cleanup() {
            resetPagedTable();
            searchReset();

            claimAnalysisSetDrilldown({
                open: false,
            });
        }
    }, [searchGetByName]);

    const { isPending : hiddenClaimPending } = useData( async () => {
        if(hideClaimPtr.value) {
            await hideOrUnHideClaimFromDashboard(hideClaimPtr.value); 
            handleRefresh();
        }
    }, [ hideClaimPtr.value ]);


    function handleLoad(sort, filter, pagination) {

        if(filter && filter.operands && filter.operands.length > 0) {
            filter.operands = filter.operands.filter(item => 
                                !item.filters.some(filter => 
                                    ['acknowledged', 'isPaid', 'flag', 'isDenial', 'contract.contractState.name'].includes(filter.id)
                                ));
        }

        let newOperands=filter?.operands?.map(operand => {
            const newFilters = operand?.filters?.map(filter => {
                if (filter.id === 'contract.name' && filter.value.includes(',') && filter.equator.includes('equal')) {
                    return {
                        ...filter,
                        value: `"${filter.value}"`
                    };
                }
                return filter;
            });
            return {
                ...operand,
                filters: newFilters
            };
        }) ?? [];
        // @ts-ignore
        let commaEscapedFilters={...filter,operands:newOperands} || null;

        claimAnalysisLanding(pagination, sort,!filter ? null : commaEscapedFilters, includeHiddenPtr.value);
        // HACK - stats will be called inside landing API to avoid render.
        // getClaimAnalysisStats(pagination, sort, filter); 
    }

    const tableFilter = useGridFilters(),
        claimPageRecord = tableFilter.pagination;

    const handleAcknowledge = ({ id }) => {
        setBulkAcknowledge(false);
        selectedClaimsIdsPtr.set([id]);
        dialogsOpenPtr.at('acknowledgeDialog').set(true);
    }

    const handleAcknowledgeClose = (shouldReloadGrid) => {
        dialogsOpenPtr.at('acknowledgeDialog').set(false);
        dialogsOpenPtr.at('flagDialog').set(false);
        setFlagToggle(false);

        if (shouldReloadGrid) {
            resetPagedTable();
            handleRefresh();
        }
    }

    function handleRefresh() {
        setRefresh(Promise.resolve());
    }

    function handleBulkAcknowledge() {
        dialogsOpenPtr.at('acknowledgeDialog').set(true);
        setBulkAcknowledge(true);
    }

    function handleDowloadTemplate() {
        let operandsWithoutBoth = tableFilter.filters.operands.filter(operand =>
            !operand.filters.some(filter => filter.value === 'both')
        );
        let filterWithoutBoth = {
            ...tableFilter.filters,
            operands: operandsWithoutBoth
        };
        const payload = {
            page: null,
            filter: filterWithoutBoth || null,
            sort: tableFilter.sort || [],
        };
        downloadAnalysisReport(payload,false, downloadTemplateCallback);
    }

    function downloadTemplateCallback(blob) {
        const displayName = `${selectedClientName.replace(/ /g, "_")}_ClaimAnalysisReport`;
        let url = window.URL.createObjectURL(blob);
        let a = document.createElement('a');
        a.href = url;
        a.download = `${displayName.replace(/\s/g, '')}.csv`;
        a.click();
    }

    const handleProcessClaimCallback = () => {
        setOpenPlayDialog(false);
    }

    const handleSingleFlag = (flag, { id }) => {
        // add optional date if flag is true
        if (flag) {
            dialogsOpenPtr.at('flagDialog').set(true);
            setFlagToggle(flag); // pre set in Flag Dialog
            setBulkFlagged(false);
            selectedClaimsIdsPtr.set([id]);
        } else {
            // directly call api on flag is false
            let payload = {
                analysisIds: [id],
                flag: flag,
                followupAlert: ''
            }
            flagClaims(payload, handleAcknowledgeClose, true);
        }
    }

    const handleHideClaim = (isHiddenFromDashboard, { id }) => {
        hideClaimPtr.set({analysisIds: [id], isHiddenFromDashboard: !isHiddenFromDashboard});
    }

    const handleBulkFlag = () => {
        dialogsOpenPtr.at('flagDialog').set(true);
        setBulkFlagged(true);
    }

    // useLayoutEffect(() => {
    //     if (firstUpdate.current) {
    //         firstUpdate.current = false;
    //         return;
    //     }
    //     handleRefresh();
    // }, [significance]);

    function handleFilterClear() {
        
            setMulti({
            filter: [],
            selectedFilter: null,
            customFilter: defaultCustomFilters?.filter(x => x.field !== 'acknowledged' && x.field !== 'isPaid' && x.field !== 'flag' && x.field !== 'isDenial' && x.field !== 'contract.contractState.name') ?? [],
            list: [],
            significance:false,
            searchTerm:"",
            ignoreAcknowledgement:false,
            showIgnoredFilterDialog:false
            });
            handleRefresh()
    }
    const setclaimSearchIgnoredDialogStatus = (obj) => {
        setMulti(obj)
        if(obj?.ignoreAcknowledgement)
        {
            handleRefresh()
        }
    }

    const selectedClaimsIdsPtr = useStatePtr<number[]>([]);

    const dialogsOpenPtr = useStatePtr(()=>({
        acknowledgeDialog: false,
        flagDialog: false,
        processClaimsDialog: false,
        updateStatusDialog: false
    }))

    return (
        <>
            <Helmet title="Confidence Score Triage" />
            <Grid container sx={{ position: "relative" }}>
                <Grid item xs={12}>
                    <BreadCrumb routes={routes} isFromClaims={true} />
                </Grid>

                <Grid
                    sx={{
                        position: "absolute",
                        right: "10px",
                    }}
                >
                    <SearchTitle
                        title="Confidence Score Triage"
                        name={"triage-landing"}
                        handleRefresh={handleRefresh}
                        shouldNotSetDefault={false}
                        showSearchCriteriaDropdown={false}
                    />
                </Grid>
            </Grid>
            <Portlet>
                <Box className={classes.statisticsContainer}>
                    <ClaimStatistics isFromTriage={true} />
                </Box>
                <PortletHeader
                    title="Confidence Score Triage"
                    name="triage-landing"
                    handleRefresh={handleRefresh}
                    showSearchFilters={true}
                    handleFilterClear={handleFilterClear}
                    includeAdvanceFilters={true}
                    toolbar={
                        <PortletHeaderToolbar>
                            <TriageTopFilters handleRefresh={handleRefresh} relativeFilter={relativeFilter} includeHiddenPtr={includeHiddenPtr} />
                        </PortletHeaderToolbar>
                    }
                />
                <PortletBody>
                    <Stack direction="column" spacing={1} sx={{ height: '100%', minHeight: 280 }}>
                        <TableLevelAction
                            handleRefresh={handleRefresh}
                            actions={ () =>
                                <>
                                    
                                </>
                            }
                            
                            loading={claimAnalysisDownloadReportPending}
                            actionLevelGrid={ () =>
                                <Grid container>
                                    <Grid item md={12}>
                                    <h6 className="">{`Total Records : ${(claimsData?.length > 0 && claimPageRecord?.totalRecords) ? claimPageRecord?.totalRecords : 0}`}</h6>
                                    </Grid>
                                </Grid>
                            }
                        />

                        <TriageDataGrid
                            defaultCustomFilter={defaultCustomFilters?.filter(x => x.field !== 'acknowledged' && x.field !== 'isPaid' && x.field !== 'flag' && x.field !== 'isDenial' && x.field !== 'contract.contractState.name')}
                            selectedClaimsIdsPtr={selectedClaimsIdsPtr}
                            data={claimsData}
                            handleAcknowledge={handleAcknowledge}
                            handleSingleFlag={handleSingleFlag}
                            loading={ pending || getByNamePending }

                            paginationFilters={tableFilter}
                            onLoad={handleLoad}
                            refresh={refresh}
                            showSearchFilters={true}
                            handleHideClaim={handleHideClaim}
                        />
                    </Stack>

                </PortletBody>
            </Portlet>

            <AcknowledgeDialog
                open={dialogsOpenPtr.value.acknowledgeDialog}
                handleClose={handleAcknowledgeClose}
                selectedItemsPtr={selectedClaimsIdsPtr}
            />

            <FlagDialog
                handleClose={handleAcknowledgeClose}
                openPtr={dialogsOpenPtr.at('flagDialog')}
                selectedItemsPtr={selectedClaimsIdsPtr}
            />

            <ProcessClaimDialog
                openPtr={dialogsOpenPtr.at('processClaimsDialog')
                    .onChange( open => {
                        if (!open) {
                            resetClaimProcessError();                        
                        }
                    })
                }
                
                selectedItemsPtr={selectedClaimsIdsPtr}

                handleProcessClaimCallback={handleProcessClaimCallback}
                claimsData={claimsData}
                filter={tableFilter.filters}
            />

            <UpdateStatusDialog 
                openPtr={dialogsOpenPtr.at('updateStatusDialog')}
                selectedItemsIdsPtr={selectedClaimsIdsPtr}
                claimStatusList={claimStatusList?.map((item)=>({label:item.name,value:item.id,name:item.name}))} 
                handleRefresh={handleRefresh}
            />
            <IgnoreFiltersDialog setclaimSearchIgnoredDialogStatus={setclaimSearchIgnoredDialogStatus}/>
        </>
    );
}